import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// web/src/components/chat/CommandPaletteModal.tsx
// Centered ⌘K modal for global capability discovery.
//
// Separate from the in-chat slash palette (see CommandPalette.tsx) — that
// one is contextual and writes into the chat input. This one is global,
// opens from anywhere via ⌘K or the TopBar button, and routes the chosen
// capability prompt into a callback (default: prefill the chat input on
// the agents page).
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/apiClient";
import { suggestCapabilities } from "../../lib/suggestCapabilities";
import { useCommandPaletteStore } from "../../stores/commandPaletteStore";
import capabilitiesData from "../../data/luna_capabilities.json";
const ALL_CAPABILITIES = capabilitiesData;
const GROUPS = [...new Set(ALL_CAPABILITIES.map((c) => c.group))];
export function CommandPaletteModal() {
    const navigate = useNavigate();
    const { open, onSelect, closePalette } = useCommandPaletteStore();
    const [query, setQuery] = useState("");
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [suggested, setSuggested] = useState([]);
    const inputRef = useRef(null);
    const selectedRef = useRef(null);
    // Fetch profile + compute suggestions when the modal opens.
    useEffect(() => {
        if (!open)
            return;
        setQuery("");
        setSelectedIndex(0);
        // Focus the input on next tick so the keystroke that opened the modal
        // doesn't accidentally type into it.
        setTimeout(() => inputRef.current?.focus(), 10);
        api
            .me()
            .then((profile) => {
            setSuggested(suggestCapabilities(ALL_CAPABILITIES, profile, 5));
        })
            .catch(() => {
            setSuggested(suggestCapabilities(ALL_CAPABILITIES, null, 5));
        });
    }, [open]);
    // Build the flat list of visible items based on the query.
    const { sections, flatList } = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (q.length === 0) {
            // No query → Suggested + grouped overview
            const out = [];
            if (suggested.length > 0) {
                out.push({ title: "Suggested for you", items: suggested });
            }
            for (const g of GROUPS) {
                const items = ALL_CAPABILITIES.filter((c) => c.group === g);
                if (items.length > 0)
                    out.push({ title: g, items });
            }
            const flat = out.flatMap((s) => s.items);
            return { sections: out, flatList: flat };
        }
        // Query → flat filtered list, grouped by category
        const matches = ALL_CAPABILITIES.filter((c) => c.group.toLowerCase().includes(q) ||
            c.label.toLowerCase().includes(q) ||
            c.description.toLowerCase().includes(q));
        const byGroup = {};
        for (const item of matches) {
            if (!byGroup[item.group])
                byGroup[item.group] = [];
            byGroup[item.group].push(item);
        }
        const out = Object.entries(byGroup).map(([title, items]) => ({ title, items }));
        return { sections: out, flatList: matches };
    }, [query, suggested]);
    // Reset selected index when the visible list changes.
    useEffect(() => {
        setSelectedIndex(0);
    }, [query]);
    // Scroll selected into view.
    useEffect(() => {
        selectedRef.current?.scrollIntoView({ block: "nearest" });
    }, [selectedIndex]);
    // Keyboard navigation.
    useEffect(() => {
        if (!open)
            return;
        function onKey(e) {
            if (e.key === "Escape") {
                e.preventDefault();
                closePalette();
                return;
            }
            if (e.key === "ArrowDown") {
                e.preventDefault();
                setSelectedIndex((i) => Math.min(i + 1, Math.max(0, flatList.length - 1)));
                return;
            }
            if (e.key === "ArrowUp") {
                e.preventDefault();
                setSelectedIndex((i) => Math.max(0, i - 1));
                return;
            }
            if (e.key === "Enter") {
                e.preventDefault();
                const item = flatList[selectedIndex];
                if (item)
                    onPick(item);
                return;
            }
        }
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, flatList, selectedIndex, closePalette]); // eslint-disable-line react-hooks/exhaustive-deps
    function onPick(item) {
        closePalette();
        if (onSelect) {
            onSelect(item.prompt);
            return;
        }
        // No callback (global ⌘K / TopBar entry point): stage the prompt in
        // the store and navigate to /. AgentsPage subscribes to the store
        // value and consumes it — works whether the user is arriving from
        // another route OR is already on / (where navigate("/") is a no-op
        // and the mount-time useEffect would never fire).
        useCommandPaletteStore.getState().setPendingChatPrompt(item.prompt);
        navigate("/");
    }
    if (!open)
        return null;
    // Build a flat index map from section position → flatList index for
    // selection highlighting + Enter handling.
    let flatIdx = 0;
    return (_jsxs("div", { className: "fixed inset-0 z-[100] flex items-start justify-center pt-[12vh] px-4", role: "dialog", "aria-modal": "true", "aria-label": "Command palette", children: [_jsx("div", { className: "fixed inset-0 bg-black/60 backdrop-blur-[2px]", onClick: closePalette }), _jsxs("div", { className: "relative w-full max-w-xl bg-slate-900 border border-slate-800 rounded-xl shadow-2xl overflow-hidden", children: [_jsxs("div", { className: "flex items-center gap-2 px-4 py-3 border-b border-slate-800", children: [_jsxs("svg", { width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", className: "text-slate-500 shrink-0", children: [_jsx("circle", { cx: "11", cy: "11", r: "7" }), _jsx("path", { d: "M21 21l-4.35-4.35" })] }), _jsx("input", { ref: inputRef, type: "text", value: query, onChange: (e) => setQuery(e.target.value), placeholder: "What if\u2026 (e.g. I retire at 55, I convert $50k to Roth)", className: "flex-1 bg-transparent text-slate-200 placeholder-slate-600 text-sm focus:outline-none" }), _jsx("kbd", { className: "text-[10px] text-slate-500 border border-slate-700 rounded px-1.5 py-0.5 hidden sm:inline", children: "esc" }), _jsx("button", { onClick: closePalette, "aria-label": "Close", className: "text-slate-500 hover:text-slate-300 transition-colors p-0.5", children: _jsxs("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", children: [_jsx("path", { d: "M18 6L6 18" }), _jsx("path", { d: "M6 6l12 12" })] }) })] }), _jsxs("div", { className: "max-h-[55vh] overflow-y-auto py-2", children: [sections.length === 0 && (_jsx("p", { className: "px-4 py-6 text-sm text-slate-500 text-center", children: "No matches" })), sections.map((section) => (_jsxs("div", { className: "pb-2", children: [_jsx("div", { className: "px-4 pt-2 pb-1", children: _jsx("span", { className: "text-[10px] font-semibold text-slate-500 uppercase tracking-widest", children: section.title }) }), section.items.map((item) => {
                                        const idx = flatIdx++;
                                        const isSelected = idx === selectedIndex;
                                        return (_jsxs("button", { ref: isSelected ? selectedRef : undefined, role: "option", "aria-selected": isSelected, onMouseEnter: () => setSelectedIndex(idx), onClick: () => onPick(item), className: `w-full text-left px-4 py-2 flex items-start gap-3 transition-colors ${isSelected ? "bg-violet-500/10" : "hover:bg-slate-800/60"}`, children: [_jsx("span", { className: "text-sm text-slate-200 basis-[38%] shrink-0 break-words leading-snug", children: item.label }), _jsx("span", { className: "text-xs text-slate-500 flex-1 min-w-0 line-clamp-2", children: item.description })] }, `${section.title}-${item.id}`));
                                    })] }, section.title)))] }), _jsxs("div", { className: "px-4 py-2 border-t border-slate-800 text-[10px] text-slate-600 flex items-center gap-3", children: [_jsxs("span", { children: [_jsx("kbd", { children: "\u2191\u2193" }), " navigate"] }), _jsxs("span", { children: [_jsx("kbd", { children: "\u21B5" }), " run"] }), _jsxs("span", { className: "ml-auto", children: [_jsx("kbd", { children: "esc" }), " close"] })] })] })] }));
}
